import { Injectable, computed, signal } from '@angular/core';
import type { PromptSuggestion } from '../colimind-types';
import { DEFAULT_SUGGESTIONS } from '../default-suggestions';

/**
 * CoLiMind settings, persisted per user in localStorage -- the same mechanism the sessions use,
 * since the backend is not available. The ticket requires the instructions to survive a reload
 * and to apply across sessions. What the instructions actually *do* is still decided
 * server-side; this service only stores them and passes them along as request metadata.
 */
export interface ColimindSettings {
  systemPrompt: string;
  /** Instructions are only sent while active; the ticket distinguishes stored from applied. */
  systemPromptActive: boolean;
  addressing: string;
  languageProfile: string;
  /** Set once the user has stepped through the onboarding to the end. */
  onboardingCompleted: boolean;
  /** When and which version of the terms of use the user accepted; empty until then. */
  termsAcceptedAt: string;
  termsVersion: string;
}

const DEFAULT_SETTINGS: ColimindSettings = {
  systemPrompt: '',
  systemPromptActive: true,
  addressing: '',
  languageProfile: '',
  onboardingCompleted: false,
  termsAcceptedAt: '',
  termsVersion: '',
};

/** Raise when the terms of use change, so every user has to accept them again. */
export const TERMS_VERSION = '1';

/** Storage key prefix; the user identifier keeps one user's settings out of another's view. */
const SETTINGS_STORAGE_PREFIX = 'COLIMIND_SETTINGS_';

export const MAX_SYSTEM_PROMPT_LEN = 2000;
export const MAX_ADDRESSING_LEN = 40;

export const MAX_SUGGESTIONS = 5;
export const MAX_SUGGESTION_TITLE_LEN = 40;
export const MAX_SUGGESTION_INSTRUCTION_LEN = 200;

/** Cuts to `max` characters, appending an ellipsis when something was actually removed. */
export function truncate(value: string, max: number): string {
  const trimmed = value.trim();
  return trimmed.length <= max ? trimmed : `${trimmed.substring(0, max - 1).trimEnd()}…`;
}

@Injectable({ providedIn: 'root' })
export class ColimindSettingsService {
  readonly settings = signal<ColimindSettings>({ ...DEFAULT_SETTINGS });
  readonly suggestions = signal<PromptSuggestion[]>([]);
  readonly hiddenDefaults = signal<string[]>([]);

  /** Draft of the suggestion currently being composed in the settings panel. */
  readonly draftTitle = signal('');
  readonly draftInstruction = signal('');

  readonly activeSuggestions = computed(() => this.suggestions().filter(s => s.active));
  readonly termsAccepted = computed(() =>
    Boolean(this.settings().termsAcceptedAt) && this.settings().termsVersion === TERMS_VERSION);
  readonly activeDefaults = computed(() => DEFAULT_SUGGESTIONS.filter(d => !this.hiddenDefaults().includes(d.id)));
  readonly canAddSuggestion = computed(() => this.suggestions().length < MAX_SUGGESTIONS);

  /** Set of the user whose settings are loaded; empty until the JWT has been read. */
  private userIdentifier = '';

  /**
   * False until a profile has been loaded. The onboarding must not flash while the stored
   * flag is still unknown, so callers wait for this before deciding to show it.
   */
  readonly profileLoaded = signal(false);

  /**
   * Binds the service to a user and loads their stored settings. Called once the JWT is known.
   * Everything written afterwards is persisted for that user.
   */
  useProfile(userIdentifier: string): void {
    this.userIdentifier = userIdentifier ?? '';
    const stored = this.read();
    if (stored) {
      this.settings.set({ ...DEFAULT_SETTINGS, ...stored.settings });
      this.suggestions.set(stored.suggestions ?? []);
      this.hiddenDefaults.set(stored.hiddenDefaults ?? []);
    }
    this.profileLoaded.set(true);
  }

  completeOnboarding(): void {
    this.update({ onboardingCompleted: true });
  }

  acceptTerms(): void {
    this.update({ termsAcceptedAt: new Date().toISOString(), termsVersion: TERMS_VERSION });
  }

  /** Lets the user step through the onboarding again from the settings. */
  replayOnboarding(): void {
    this.update({ onboardingCompleted: false });
  }

  update(patch: Partial<ColimindSettings>): void {
    this.settings.update(s => ({ ...s, ...patch }));
    this.persist();
  }

  private storageKey(): string {
    return SETTINGS_STORAGE_PREFIX + this.userIdentifier;
  }

  private read(): { settings?: Partial<ColimindSettings>; suggestions?: PromptSuggestion[]; hiddenDefaults?: string[] } | null {
    if (!this.userIdentifier) return null;
    const raw = localStorage.getItem(this.storageKey());
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      // A corrupt entry must not keep the panel from working.
      localStorage.removeItem(this.storageKey());
      return null;
    }
  }

  /** Writes silently; settings are a convenience, a full storage must not break the chat. */
  private persist(): void {
    if (!this.userIdentifier) return;
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify({
        settings: this.settings(),
        suggestions: this.suggestions(),
        hiddenDefaults: this.hiddenDefaults(),
      }));
    } catch {
      // Storage full or unavailable -- keep the values in memory for this session.
    }
  }

  /** Builds the request metadata object from the current settings (empty values omitted). */
  toMetadata(threadId?: string): Record<string, string> {
    const s = this.settings();
    const meta: Record<string, string> = {};
    // Deactivated or over-long instructions are kept but not applied, so the assistant never
    // acts on something the user was told is not in effect.
    const prompt = s.systemPrompt.trim();
    if (prompt && s.systemPromptActive && prompt.length <= MAX_SYSTEM_PROMPT_LEN) {
      meta['system_prompt'] = prompt;
    }
    if (s.addressing.trim()) meta['addressing'] = truncate(s.addressing, MAX_ADDRESSING_LEN);
    if (s.languageProfile.trim()) meta['language_profile'] = s.languageProfile.trim();
    if (threadId) meta['thread_id'] = threadId; // the thread id gets added to the MAUI request inside colimo-ws-colimind in order to use the same thread/Session
    return meta;
  }

  setDraft(title: string, instruction: string): void {
    this.draftTitle.set(truncate(title, MAX_SUGGESTION_TITLE_LEN));
    this.draftInstruction.set(truncate(instruction, MAX_SUGGESTION_INSTRUCTION_LEN));
  }

  clearDraft(): void {
    this.draftTitle.set('');
    this.draftInstruction.set('');
  }

  /** Adds the current draft as a suggestion. Returns false when the limit is reached. */
  addDraftAsSuggestion(): boolean {
    if (!this.canAddSuggestion()) return false;
    const title = truncate(this.draftTitle(), MAX_SUGGESTION_TITLE_LEN);
    const instruction = truncate(this.draftInstruction(), MAX_SUGGESTION_INSTRUCTION_LEN);
    if (!title || !instruction) return false;

    this.suggestions.update(s => [...s, {
      id: `sug_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      title,
      instruction,
      active: true,
    }]);
    this.clearDraft();
    this.persist();
    return true;
  }

  removeSuggestion(id: string): void {
    this.suggestions.update(s => s.filter(sug => sug.id !== id));
    this.persist();
  }

  toggleDefault(id: string): void {
    this.hiddenDefaults.update(h => (h.includes(id) ? h.filter(x => x !== id) : [...h, id]));
    this.persist();
  }

  toggleSuggestion(id: string): void {
    this.suggestions.update(s => s.map(sug =>
      sug.id === id ? { ...sug, active: !sug.active } : sug
    ));
    this.persist();
  }
}
